import type { HubMetricConfig } from "@expert/app-config";

import { useIsPacingActive, useTodayPacingMetrics, useTodaySales, useTotalCallCount } from "@expert/sales-tools";
import { SimpleGrid } from "@mantine/core";
import { useMemo } from "react";

import { usePartnerConfig } from "@/partner/usePartnerConfig";

import { ConfigMetricCard } from "../cards/ConfigMetricCard";
import { EMPTY_HUB_METRICS } from "../emptyHubMetrics";
import { useHubPanelStore } from "../right-panel/useHubPanelStore";

export function isLiveCardVisible(card: HubMetricConfig, isPacingActive: boolean): boolean {
    if (card.source.kind !== "gamification") return false;
    if (card.source.metric === "pacing") return isPacingActive;
    return true;
}

function deriveCardError(isLoading: boolean, isError: boolean, value: number | null): boolean {
    return !isLoading && (isError || value === null);
}

function LiveTotalCallsCard({ config }: { config: HubMetricConfig }) {
    const { data, isLoading, isError } = useTotalCallCount();
    const value = data?.callCount ?? null;
    return (
        <ConfigMetricCard
            config={config}
            displayValue={value}
            isLoading={isLoading}
            isError={deriveCardError(isLoading, isError, value)}
            isEmpty={false}
            // grade is false to not punish if call volume is low. no current targets exist for this metric.
            grade={false}
        />
    );
}

function LivePaceCard({ config }: { config: HubMetricConfig }) {
    const { data, isLoading, isError } = useTodayPacingMetrics();
    const hasNoSessions = !!data && data.totalSessions === 0;
    const hasError = !isLoading && (isError || !data);
    const value = data && data.totalSessions > 0 ? Math.round(data.onTargetRate * 100) : null;
    return (
        <ConfigMetricCard
            config={config}
            displayValue={value}
            isLoading={isLoading}
            isError={hasError}
            isEmpty={!isLoading && !hasError && hasNoSessions}
            emptyValue="No calls today"
        />
    );
}

function LiveSalesCard({ config }: { config: HubMetricConfig }) {
    const { data, isLoading, isError } = useTodaySales();
    const togglePanel = useHubPanelStore((s) => s.togglePanel);
    const value = data?.salesCount ?? null;
    return (
        <ConfigMetricCard
            config={config}
            displayValue={value}
            isLoading={isLoading}
            isError={deriveCardError(isLoading, isError, value)}
            isEmpty={false}
            // for live sales there is a target but we dont punish with off target status pill
            grade={false}
            onClick={() => togglePanel("today-sales")}
        />
    );
}

function renderLiveCard(card: HubMetricConfig) {
    if (card.source.kind !== "gamification") return null;
    switch (card.source.metric) {
        case "totalCalls":
            return <LiveTotalCallsCard key={card.id} config={card} />;
        case "sales":
            return <LiveSalesCard key={card.id} config={card} />;
        case "pacing":
            return <LivePaceCard key={card.id} config={card} />;
        default:
            return null;
    }
}

export function TodayMetrics() {
    const isPacingActive = useIsPacingActive();
    const { hubMetrics } = usePartnerConfig();

    const liveCards = useMemo(
        () => (hubMetrics ?? EMPTY_HUB_METRICS).filter((card) => isLiveCardVisible(card, isPacingActive)),
        [hubMetrics, isPacingActive],
    );

    return (
        <SimpleGrid type="container" cols={{ base: 1, "440px": 2, "680px": 3 }} spacing="md">
            {liveCards.map((card) => renderLiveCard(card))}
        </SimpleGrid>
    );
}
